import cx from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Button } from '../button';
import { Icon, IconColor, IconID, IconSize } from '../icon';
import { BASE_ICON_SPRITE_PATH } from '../icon/icon-constants';
import { Typography } from '../typography';
import styles from './date-picker.module.scss';
import { getArialLabelForArrowButtons, getYearsPickerRangeStart, } from './date-picker.util';
import DatePickerInput from './date-picker-input';
import { DatePickerRange } from './range';
export const popperOffsetDefaults = {
    // Position for popper: X and Y-axis offsets from default positions.
    right: [0, 12],
    bottom: [62, 0],
};
export const datePickerFlipDefaults = {
    /**
     * Define an array of allowed positions to restrict flipping.
     * Will flip based on viewport when not specified.
     */
    bottom: ['bottom'],
};
const CustomHeader = ({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled, decreaseYear, increaseYear, prevYearButtonDisabled, nextYearButtonDisabled, showMonthPicker, setShowMonthPicker, showYearPicker, setShowYearPicker, getYearsPickerRangeStart, }) => {
    return (React.createElement("div", { style: {
            marginBottom: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        } },
        React.createElement(Button, { icon: IconID.CHEVRON_LEFT, type: "tertiary", "data-testid": "leftArrowButton", onClick: showMonthPicker || showYearPicker
                ? decreaseYear
                : decreaseMonth, isDisabled: showMonthPicker
                ? prevYearButtonDisabled
                : prevMonthButtonDisabled, className: cx(styles.button, styles.iconButton), ariaLabel: getArialLabelForArrowButtons(true, showMonthPicker, showYearPicker) }),
        !showMonthPicker && !showYearPicker && (React.createElement("div", null,
            React.createElement("button", { type: "button", "data-testid": "monthPickerButton", className: cx(styles.button, styles.textButton), onClick: setShowMonthPicker }, moment(date).format('MMMM')),
            React.createElement("button", { type: "button", "data-testid": "yearPickerButton", className: cx(styles.button, styles.textButton), onClick: setShowYearPicker }, moment(date).format('yyyy')))),
        showMonthPicker && (React.createElement(Typography, { variant: "content-label-subhead", noMargin: true }, moment(date).format('yyyy'))),
        showYearPicker && (React.createElement(Typography, { variant: "content-label-subhead", noMargin: true }, getYearsPickerRangeStart(date))),
        React.createElement(Button, { icon: IconID.CHEVRON_RIGHT, type: "tertiary", "data-testid": "rightArrowButton", onClick: showMonthPicker || showYearPicker
                ? increaseYear
                : increaseMonth, isDisabled: showMonthPicker
                ? nextYearButtonDisabled
                : nextMonthButtonDisabled, className: cx(styles.button, styles.iconButton), ariaLabel: getArialLabelForArrowButtons(false, showMonthPicker, showYearPicker) })));
};
export const DatePicker = ({ id, className, children, dateFormat = 'MM/dd/yyyy', selected = null, onChange, label, underLineText, hasError, disabled, placeholderText, position = 'bottom', allowPositionToFlip, popperOffset, hideClearButton = false, ...restProps }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showMonthPicker, setShowMonthPicker] = useState(false);
    const [showYearPicker, setShowYearPicker] = useState(false);
    const [active, setActive] = useState(false);
    const [inputKey, setInputKey] = useState(0);
    const containerClassNames = cx(className, styles.container, {
        [styles.disabled]: disabled,
    }, { [styles.hasError]: hasError }, { [styles.active]: active });
    const handleMonth = () => {
        setShowMonthPicker(true);
    };
    const handleYear = () => {
        setShowYearPicker(true);
    };
    const handleFocus = () => {
        if (!hasError)
            setActive(true);
        setIsOpen(true);
    };
    const resetDatePicker = () => {
        setShowMonthPicker(false);
        setShowYearPicker(false);
    };
    const reRenderInput = () => {
        setInputKey(inputKey + 1);
    };
    const preventPropagation = (event) => {
        event.stopPropagation();
        event.preventDefault();
    };
    const handleDateSelect = (date, event) => {
        var _a;
        preventPropagation(event);
        if (((_a = event.target) === null || _a === void 0 ? void 0 : _a.type) === 'text') {
            return;
        }
        if (!showYearPicker && !showMonthPicker) {
            setIsOpen(false);
            onChange(date, event);
        }
        else if (showYearPicker) {
            setShowYearPicker(false);
            setShowMonthPicker(true);
        }
        else {
            setShowMonthPicker(false);
        }
    };
    const handleChange = (date, event) => {
        var _a;
        preventPropagation(event);
        if (((_a = event.target) === null || _a === void 0 ? void 0 : _a.type) === 'text') {
            return;
        }
        if (!date) {
            handleDateSelect(date, event);
        }
    };
    const handleEscape = (event) => {
        preventPropagation(event);
        setIsOpen(false);
    };
    const handleInputValue = (event) => {
        var _a;
        const inputValue = (_a = event.target) === null || _a === void 0 ? void 0 : _a.value;
        if (inputValue) {
            const formattedMomentDate = moment(inputValue, dateFormat.toUpperCase());
            if (formattedMomentDate.isValid() &&
                _.isEmpty(formattedMomentDate.parsingFlags().unusedInput)) {
                onChange(formattedMomentDate.toDate(), event);
            }
            else {
                onChange(new Date(), event);
            }
        }
        else {
            onChange(null, event);
        }
    };
    const handleEnter = (event) => {
        preventPropagation(event);
        setIsOpen(false);
        handleInputValue(event);
        reRenderInput();
        setIsOpen(false);
        resetDatePicker();
    };
    const handleChangeRaw = (event) => {
        handleInputValue(event);
    };
    const popperPositionModifiers = useMemo(() => {
        const offset = popperOffset !== null && popperOffset !== void 0 ? popperOffset : popperOffsetDefaults[position];
        let flip = datePickerFlipDefaults[position];
        // Explicit boolean checks to not change values with `undefined`.
        if (allowPositionToFlip === true) {
            flip = undefined;
        }
        else if (allowPositionToFlip === false) {
            flip = [position];
        }
        return {
            /**
             * Tell popper to try not to stay within the view.
             * This prevents the popper from covering the element you clicked.
             */
            preventOverflow: { enabled: false },
            // Turn off since it needs `preventOverflow` to be enabled.
            hide: { enabled: false },
            flip: { flip },
            offset: { offset: `${offset[0]}, ${offset[1]}` },
        };
    }, [position, allowPositionToFlip, popperOffset]);
    return (React.createElement("div", { className: containerClassNames },
        React.createElement("link", { rel: "preload", href: BASE_ICON_SPRITE_PATH, as: "image" }),
        label && (React.createElement(Typography, { variant: "footnote", className: styles.label }, label)),
        React.createElement(ReactDatePicker, { formatWeekDay: (dayName) => dayName.substring(0, 1), dateFormat: dateFormat, selected: selected, dropdownMode: "select", onSelect: handleDateSelect, onChange: handleChange, onFocus: handleFocus, onChangeRaw: handleChangeRaw, open: isOpen, popperPlacement: position, popperModifiers: popperPositionModifiers, customInput: React.createElement(DatePickerInput
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            , { 
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                hasError: hasError, placeholderText: placeholderText, key: inputKey, onEnter: handleEnter, onEscape: handleEscape }), focusSelectedMonth: true, isClearable: !hideClearButton, disabled: disabled, showMonthYearPicker: showMonthPicker, 
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            showFourColumnMonthYearPicker: true, showYearPicker: showYearPicker, renderCustomHeader: ({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled, decreaseYear, increaseYear, prevYearButtonDisabled, nextYearButtonDisabled, }) => (React.createElement(CustomHeader, { date: date, decreaseMonth: decreaseMonth, increaseMonth: increaseMonth, prevMonthButtonDisabled: prevMonthButtonDisabled, nextMonthButtonDisabled: nextMonthButtonDisabled, decreaseYear: decreaseYear, increaseYear: increaseYear, prevYearButtonDisabled: prevYearButtonDisabled, nextYearButtonDisabled: nextYearButtonDisabled, showMonthPicker: showMonthPicker, setShowMonthPicker: handleMonth, showYearPicker: showYearPicker, setShowYearPicker: handleYear, getYearsPickerRangeStart: getYearsPickerRangeStart })), onClickOutside: () => {
                resetDatePicker();
                setIsOpen(false);
                setActive(false);
            }, onCalendarClose: () => {
                setActive(false);
            }, onCalendarOpen: () => {
                resetDatePicker();
                setIsOpen(true);
            }, ...restProps }, children),
        underLineText && (React.createElement("div", { className: styles.underlineWrapper },
            hasError && (React.createElement(Icon, { color: IconColor.RED, size: IconSize.XSMALL, id: IconID.EXCLAMATION_POINT_CIRCLE, className: styles.underlineIcon })),
            React.createElement(Typography, { variant: "footnote", className: styles.underlineText }, underLineText)))));
};
DatePicker.Range = DatePickerRange;
//# sourceMappingURL=date-picker.js.map